import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import {
  AccessTab,
  CPF_FILTER,
  SetTabFilterPayload,
  StoreAccessFilterState,
  StoreHealthEntry,
  TabFilters,
} from "./types";

/**
 * Defaults de cada tab. Extraído como constante exportada para os testes
 * unitários travarem o contrato de reset.
 */
export const DEFAULT_TAB_FILTERS: TabFilters = {
  search: "",
  code: "",
  classification: null,
  cpfFilter: CPF_FILTER.VALID,
  blocked: null,
  situation: null,
  sortBy: "name",
  sortOrder: "ASC",
  page: 1,
  perPage: 10,
};

/**
 * Estado inicial. `storeCode=43` preserva o comportamento de auto-seleção
 * atual (Opção A da discussão do PRD). `activeTab="users"` é o default ao
 * abrir a tela pela primeira vez.
 */
const INITIAL_STATE: StoreAccessFilterState = {
  storeCode: 43,
  activeTab: "users",
  users: { ...DEFAULT_TAB_FILTERS },
  operators: { ...DEFAULT_TAB_FILTERS },
  storeHealth: {},
};

const storeAccessFilterSlice = createSlice({
  name: "storeAccessFilter",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Atualiza a loja selecionada e reseta filtros/paginação de AMBAS as tabs
     * atomicamente. Preserva `activeTab` e `storeHealth`.
     *
     * Justificativa: trocar de loja significa contexto totalmente novo — os
     * filtros aplicados na loja anterior quase nunca fazem sentido na nova.
     * Resetar evita resultados confusos.
     */
    setStoreCode(state, action: PayloadAction<number | null>) {
      state.storeCode = action.payload;
      state.users = { ...DEFAULT_TAB_FILTERS };
      state.operators = { ...DEFAULT_TAB_FILTERS };
    },
    /**
     * Troca a tab ativa preservando o estado de filtros de ambas.
     */
    setActiveTab(state, action: PayloadAction<AccessTab>) {
      state.activeTab = action.payload;
    },
    /**
     * Atualiza um filtro de UMA tab específica — isolado por design.
     */
    setTabFilter(state, action: PayloadAction<SetTabFilterPayload>) {
      const { tab, key, value } = action.payload;
      // O callsite garante o pareamento key↔value via SetTabFilterPayload<K>;
      // aqui usamos Object.assign para evitar que TS degrade value para `never`
      // quando key fica apenas como keyof TabFilters (perde a relação genérica).
      Object.assign(state[tab], { [key]: value });
    },
    /**
     * Reseta todos os filtros/paginação de UMA tab. Usado pelo botão "limpar
     * filtros" da tab (independente da outra).
     */
    resetTabFilters(state, action: PayloadAction<AccessTab>) {
      state[action.payload] = { ...DEFAULT_TAB_FILTERS };
    },
    /**
     * Registra saúde de uma loja (volátil — não persistido).
     */
    setStoreHealth(
      state,
      action: PayloadAction<{ storeCode: number; health: StoreHealthEntry }>,
    ) {
      state.storeHealth[action.payload.storeCode] = action.payload.health;
    },
  },
});

export const {
  setStoreCode,
  setActiveTab,
  setTabFilter,
  resetTabFilters,
  setStoreHealth,
} = storeAccessFilterSlice.actions;

/**
 * Configuração de persistência específica do slice (Opção 2 do PRD).
 * - `storeCode`, `activeTab`, `users`, `operators` são persistidos.
 * - `storeHealth` NÃO é persistido (estado volátil — sensação de saúde muda
 *   a cada sessão, e serializar Sinon/ErrorEntry não agrega valor).
 */
export const storeAccessFilterPersistConfig = {
  key: "storeAccessFilter",
  storage,
  blacklist: ["storeHealth"],
};

/**
 * Reducer do slice **já envolvido em redux-persist**. O root store combina
 * este reducer no `combineReducers` normalmente — a persistência acontece
 * transparentemente. Elimina a necessidade de `useEffect + localStorage`
 * manual em componentes (era a gambiarra pré-Story-1.5 em `SelectFilters`).
 */
export default persistReducer(
  storeAccessFilterPersistConfig,
  storeAccessFilterSlice.reducer,
);

/**
 * Reducer "puro" (sem redux-persist) — exportado APENAS para testes
 * unitários. Consumidores em runtime devem usar o default export.
 */
export const rawStoreAccessFilterReducer = storeAccessFilterSlice.reducer;
